import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  MenuItem,
  FormControl,
  CircularProgress,
  Button,
  Box,
} from '@material-ui/core';
import JSONPrettyMon from 'react-json-pretty/dist/monikai';
import { CSVLink } from 'react-csv';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { formatFloatNumber } from '../../../../utils/functions';
import {
  FlexCenterContainer,
  StyledBox,
  StyledCSVLinkContainer,
  StyledJSONPretty,
  StyledXMLViewer,
  StyledLoadingContainer,
  StyledResultContainer,
  StyledInputLabel,
  StyledSelect,
} from './TabPanel.styled';
import {
  getTrafficStatus,
  getLoadingTrafficStatus,
} from '../../../../state/selectors/getPublicData.selector';
import { useSelector } from 'react-redux';
import { parse as js2xmlParse } from 'js2xmlparser';
import { transforms as jsonTransforms, Parser } from 'json2csv';

const xmlTheme = {
  separatorColor: '#66d9ef',
  tagColor: '#f92672',
  textColor: '#a6e22e',
  attributeValueColor: '#fd971f',
  attributeKeyColor: '#66d9ef',
};
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
      style={{ maxHeight: 600 }}
    >
      {value === index && <StyledBox p={3}>{children}</StyledBox>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    minWidth: 500,
    marginTop: 16,
    paddingBottom: 0,
    marginBottom: 0,
  },
}));

export default function FullWidthTabs() {
  const classes = useStyles();
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const [copied, setCopied] = useState(false);

  const trafficStatus = useSelector(getTrafficStatus);
  const loadingTrafficStatus = useSelector(getLoadingTrafficStatus);

  const handleChangeDataToCsv = (trafficStatus) => {
    // config for csv file
    let startTime = '';
    let endTime = '';
    let csvData = '';
    if (trafficStatus && trafficStatus.length > 0) {
      const transforms = jsonTransforms.flatten({
        objects: true,
        arrays: true,
      });
      const parser = new Parser({ transforms });
      csvData = parser.parse(trafficStatus);
      startTime = trafficStatus[0].time;
      endTime = trafficStatus[trafficStatus.length - 1].time;
    }
    return {
      data: csvData,
      filenameCSV: `traffic_status(${startTime}${
        endTime && '-'
      }${endTime}).csv`,
      filenameXlsx: `traffic_status(${startTime}${
        endTime && '-'
      }${endTime}).xlsx`,
    };
  };

  const xmlData = js2xmlParse('trafficStatus', trafficStatus);

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  // format traffic status for csv
  let formatTrafficStatus = [];
  if (trafficStatus && trafficStatus.length > 0) {
    formatTrafficStatus = trafficStatus.map((item) => {
      const newData = { ...item };
      const tempCoordinates = [...item.polyline.coordinates];
      const formatTempCoordinates = tempCoordinates.map((coordinate) =>
        coordinate.map((item) => formatFloatNumber(item))
      );
      const tempPolyline = {
        ...item.polyline,
        coordinates: formatTempCoordinates,
      };
      newData.polyline = tempPolyline;
      return newData;
    });
  }

  useEffect(() => {
    setCopied(false);
  }, [value, trafficStatus]);

  const CSV = handleChangeDataToCsv(formatTrafficStatus);

  return (
    <div className={classes.root}>
      <FlexCenterContainer>
        <FormControl className={classes.formControl}>
          <StyledInputLabel id='controlled-open-select-label'>
            Format
          </StyledInputLabel>
          <StyledSelect
            labelId='controlled-open-select-label'
            id='controlled-open-select'
            style={{ width: 100 }}
            value={value}
            defaultValue={0}
            onChange={handleChange}
          >
            <MenuItem value={0}>JSON</MenuItem>
            <MenuItem value={1}>XML</MenuItem>
            <MenuItem value={2}>CSV</MenuItem>
            <MenuItem value={3}>XLSX</MenuItem>
          </StyledSelect>
        </FormControl>
      </FlexCenterContainer>
      {loadingTrafficStatus ? (
        <StyledLoadingContainer>
          <CircularProgress />
        </StyledLoadingContainer>
      ) : (
        <>
          {value === 0 && (
            <Box display='flex' justifyContent='center' mt={2}>
              <CopyToClipboard
                text={JSON.stringify(trafficStatus)}
                onCopy={() => setCopied(true)}
              >
                <Button variant='contained' color='secondary'>
                  {!copied ? 'Copy Data' : 'Data copied!'}
                </Button>
              </CopyToClipboard>
            </Box>
          )}
          {value === 1 && (
            <Box display='flex' justifyContent='center' mt={2}>
              <CopyToClipboard text={xmlData} onCopy={() => setCopied(true)}>
                <Button variant='contained' color='secondary'>
                  {!copied ? 'Copy Data' : 'Data copied!'}
                </Button>
              </CopyToClipboard>
            </Box>
          )}
          <StyledResultContainer>
            <TabPanel value={value} index={0} dir={theme.direction}>
              <StyledJSONPretty
                data={trafficStatus}
                theme={JSONPrettyMon}
              ></StyledJSONPretty>
            </TabPanel>
            <TabPanel value={value} index={1} dir={theme.direction}>
              <StyledXMLViewer xml={xmlData} theme={xmlTheme}></StyledXMLViewer>
            </TabPanel>
            <TabPanel value={value} index={2} dir={theme.direction}>
              <StyledCSVLinkContainer>
                {formatTrafficStatus.length > 0 && (
                  <CSVLink data={CSV.data} filename={CSV.filenameCSV}>
                    Download now
                  </CSVLink>
                )}
              </StyledCSVLinkContainer>
            </TabPanel>
            <TabPanel value={value} index={3} dir={theme.direction}>
              <StyledCSVLinkContainer>
                {formatTrafficStatus.length > 0 && (
                  <CSVLink data={CSV.data} filename={CSV.filenameXlsx}>
                    Download now
                  </CSVLink>
                )}
              </StyledCSVLinkContainer>
            </TabPanel>
          </StyledResultContainer>
        </>
      )}
    </div>
  );
}
